'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';

/**
 * Push a single command onto the Taboola queue. Safe to call before loader.js
 * has finished loading — `_taboola` is a plain array until the library replaces
 * it with its live API, and either form accepts `.push()`.
 */
export function pushTaboola(cmd: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as {
    _taboola?: unknown[];
  };
  w._taboola = w._taboola || [];
  w._taboola.push(cmd);
}

// Taboola wants a single `{flush:true}` *after* all placements for a given
// "page" have been pushed. React mounts widgets in separate effects, so we
// debounce: every widget that mounts (re)arms this timer, and the flush fires
// once the burst settles. Infinite scroll re-arms it per scrolled-in article.
let flushTimer: ReturnType<typeof setTimeout> | null = null;
export function scheduleTaboolaFlush(): void {
  if (typeof window === 'undefined') return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    pushTaboola({
      flush: true
    });
  }, 80);
}
interface TaboolaWidgetProps {
  /** Taboola display mode, e.g. `thumbnails-mid-3x2`, `alternating-thumbnails-a`. */
  mode: string;
  /** DOM id of the container div. Must be unique per page (Taboola requirement). */
  container: string;
  /** Backstage placement name, e.g. `Mid Homepage Thumbnails`. */
  placement: string;
  /** Taboola target type — `mix` for all onboarding placements. */
  targetType?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders one Taboola widget placement — the "body code" from the onboarding
 * snippet (`<div id=…>` + `_taboola.push({mode, container, placement, …})`),
 * ported to React. Self-skips the push on Lighthouse/PageSpeed (the container
 * div still renders, but stays empty, so layout is identical for synthetic runs).
 *
 * The container `<div>` always renders (SSR + client); only the queue push is
 * gated, and it fires exactly once per mount.
 */
export default function TaboolaWidget(t0) {
  const $ = _c(10);
  const {
    mode,
    container,
    placement,
    targetType: t1,
    className,
    style
  } = t0;
  const targetType = t1 === undefined ? "mix" : t1;
  const pushedRef = useRef(false);
  let t2;
  let t3;
  if ($[0] !== container || $[1] !== mode || $[2] !== placement || $[3] !== targetType) {
    t2 = () => {
      if (pushedRef.current || isLighthouseClient()) {
        return;
      }
      pushedRef.current = true;
      pushTaboola({
        mode,
        container,
        placement,
        target_type: targetType
      });
      scheduleTaboolaFlush();
    };
    t3 = [mode, container, placement, targetType];
    $[0] = container;
    $[1] = mode;
    $[2] = placement;
    $[3] = targetType;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== className || $[7] !== container || $[8] !== style) {
    t4 = <div id={container} className={className} style={style} />;
    $[6] = className;
    $[7] = container;
    $[8] = style;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}